/**
 * Scan feedback — local-first perception channel for the CHIMERA lens.
 *
 * Each viewer's response to a body scan ("landed" / "didn't land" / "neutral")
 * is stored in localStorage only. No remote writes, no analytics, no extraction.
 * The aggregation surface is consumed later by the continuity shape-rendering.
 */

export type ScanVerdict = 'landed' | 'didnt_land' | 'neutral';

export interface ScanFeedbackRecord {
  mediaType: 'movie' | 'tv';
  mediaId: number;
  verdict: ScanVerdict;
  note?: string;
  clusters: string[];
  timestamp: number;
}

const FEEDBACK_PREFIX = 'cinevault_scan_feedback_v1';
const FEEDBACK_INDEX_KEY = 'cinevault_scan_feedback_index_v1';

function recordKey(mediaType: 'movie' | 'tv', mediaId: number): string {
  return `${FEEDBACK_PREFIX}_${mediaType}_${mediaId}`;
}

function readIndex(): string[] {
  try {
    const raw = localStorage.getItem(FEEDBACK_INDEX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === 'string') : [];
  } catch {
    return [];
  }
}

function writeIndex(entries: string[]): void {
  try {
    localStorage.setItem(FEEDBACK_INDEX_KEY, JSON.stringify(entries));
  } catch {
    // Ignore quota / privacy-mode failures.
  }
}

export function getFeedbackFor(mediaType: 'movie' | 'tv', mediaId: number): ScanFeedbackRecord | null {
  try {
    const raw = localStorage.getItem(recordKey(mediaType, mediaId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ScanFeedbackRecord>;
    if (
      (parsed.mediaType !== 'movie' && parsed.mediaType !== 'tv') ||
      typeof parsed.mediaId !== 'number' ||
      (parsed.verdict !== 'landed' && parsed.verdict !== 'didnt_land' && parsed.verdict !== 'neutral') ||
      !Array.isArray(parsed.clusters) ||
      typeof parsed.timestamp !== 'number'
    ) {
      return null;
    }
    return {
      mediaType: parsed.mediaType,
      mediaId: parsed.mediaId,
      verdict: parsed.verdict,
      note: typeof parsed.note === 'string' ? parsed.note : undefined,
      clusters: parsed.clusters.filter((entry): entry is string => typeof entry === 'string'),
      timestamp: parsed.timestamp,
    };
  } catch {
    return null;
  }
}

export function saveFeedback(record: ScanFeedbackRecord): void {
  try {
    const key = recordKey(record.mediaType, record.mediaId);
    localStorage.setItem(key, JSON.stringify(record));
    const indexEntry = `${record.mediaType}_${record.mediaId}`;
    const index = readIndex();
    if (!index.includes(indexEntry)) {
      writeIndex([...index, indexEntry]);
    }
  } catch {
    // Ignore quota / privacy-mode failures.
  }
}

export function getAllFeedback(): ScanFeedbackRecord[] {
  const index = readIndex();
  const records: ScanFeedbackRecord[] = [];
  for (const entry of index) {
    const [type, idRaw] = entry.split('_');
    if ((type !== 'movie' && type !== 'tv') || !idRaw) continue;
    const id = Number.parseInt(idRaw, 10);
    if (!Number.isFinite(id)) continue;
    const record = getFeedbackFor(type, id);
    if (record) records.push(record);
  }
  return records;
}

/**
 * Aggregate cluster counts for scans the viewer marked as "landed".
 * Used by the continuity shape-rendering (Phase 3) to surface the viewer's
 * substrate-shape across what they've engaged with.
 */
export function getLandedClusterCounts(): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const record of getAllFeedback()) {
    if (record.verdict !== 'landed') continue;
    for (const cluster of record.clusters) {
      counts[cluster] = (counts[cluster] ?? 0) + 1;
    }
  }
  return counts;
}
